import React, { useState, useMemo } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, makeStyles,
} from '@material-ui/core';
import moment from 'moment';
import Pagination from '../../logisticsDashboard/components/Pagination';


const fmtDateTime = (v) => {
  if (!v) return '';
  const m = moment(v);
  if (!m.isValid()) return '';
  return m.format('DD-MMM-YYYY\nHH:mm:ss').toUpperCase();
};

const useStyles = makeStyles(() => ({
  tableContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    margin: '8px 0',
    overflowX: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  scrollArea: {
    width: '100%',
    maxHeight: 440,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  headerRow: {
    backgroundColor: '#F9F9FC',
    '& th': {
      backgroundColor: '#F9F9FC',
      fontWeight: 600,
      fontSize: '0.88rem',
      color: '#333',
      padding: '8px 12px',
      borderBottom: '1px solid #e0e0e0',
      verticalAlign: 'bottom',
      lineHeight: 1.3,
      whiteSpace: 'normal',
      wordBreak: 'normal',
      overflowWrap: 'normal',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
  },
  bodyRow: {
    '&:hover': { backgroundColor: '#f5f5f5' },
    '& td': {
      fontSize: '0.9rem',
      padding: '8px 12px',
      borderBottom: '1px solid #eeeeee',
      color: '#444',
      verticalAlign: 'top',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  rowHeaderCell: {
    fontWeight: 700,
    fontSize: '0.9rem',
    color: '#333',
    backgroundColor: '#F9F9FC',
    padding: '8px 12px',
    borderBottom: '1px solid #eeeeee',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  dateTime: {
    display: 'block',
    whiteSpace: 'nowrap',
    lineHeight: 1.4,
  },
}));

const getRowLabel = (row, idx, rows) => {
  if (idx === 0) return 'Start Procedure\nNumber';
  if (idx === rows.length - 1) return 'End Procedure\nNumber';
  return `Procedure\nNumber`;
};

const getProcedureNumber = (row) => {
  const unit = row.unitProcedure || '';
  const op = row.operation || '';
  const phase = row.phase || '';
  if (unit && op && phase) return `${unit}.${op}.${phase}`;
  if (unit && op) return `${unit}.${op}`;
  return unit || row.procedureNumber || '';
};

const CycleTimeTab = ({ data, loading, pageSize = 10 }) => {
  const classes = useStyles();
  const PAGE_SIZE = pageSize || 10;
  const rows = data || [];

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [rows, safePage, PAGE_SIZE]
  );

  if (loading) return <Box style={{ textAlign: 'center', padding: 24 }}>Loading...</Box>;

  return (
    <Paper className={classes.tableContainer} elevation={0}>
      <div className={classes.scrollArea}>
        <Table stickyHeader size="small" style={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow className={classes.headerRow}>
              <TableCell style={{ width: '130px' }} />
              <TableCell>Procedure<br />Number</TableCell>
              <TableCell>Procedure<br />Name</TableCell>
              <TableCell>Actual<br />Start Time</TableCell>
              <TableCell>Actual<br />End Time</TableCell>
              <TableCell>Planned<br />Cycle Time</TableCell>
              <TableCell>Actual<br />Cycle Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} style={{ textAlign: 'center' }}>No data available</TableCell></TableRow>
            ) : pageRows.map((row, i) => {
              const idx = (safePage - 1) * PAGE_SIZE + i;
              return (
              <TableRow key={idx} className={classes.bodyRow}>
                <TableCell className={classes.rowHeaderCell}>
                  {getRowLabel(row, idx, rows).split('\n').map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </TableCell>
                <TableCell>{getProcedureNumber(row)}</TableCell>
                <TableCell>{row.procedureName}</TableCell>
                <TableCell>
                  {['20-JAN-2026\n10:30:00', '22-JAN-2026\n08:30:00'][idx]?.split('\n').map((line, i) => (
                    <span key={i} className={classes.dateTime}>{line}</span>
                  )) || fmtDateTime(row.actualStartDate || row.actualStartTime?.date).split('\n').map((line, i) => (
                    <span key={i} className={classes.dateTime}>{line}</span>
                  ))}
                </TableCell>
                <TableCell>
                  {['20-JAN-2026\n10:30:00', '22-JAN-2026\n08:30:00'][idx]?.split('\n').map((line, i) => (
                    <span key={i} className={classes.dateTime}>{line}</span>
                  )) || fmtDateTime(row.actualEndDate || row.actualEndTime?.date).split('\n').map((line, i) => (
                    <span key={i} className={classes.dateTime}>{line}</span>
                  ))}
                </TableCell>
                <TableCell>{row.plannedCycleTimeStr || row.plannedCycleTime || ''}</TableCell>
                <TableCell>{row.actualCycleTimeStr || row.actualCycleTime || ''}</TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {rows.length > PAGE_SIZE && (
        <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
      )}
    </Paper>
  );
};

export default CycleTimeTab;
